import { httpsCallable } from 'firebase/functions'
import {
  deferredEventBatchingConfig,
  isDeferredEventActionEnabled,
  type DeferredEventActionKey,
} from '../config/deferredEventBatching'
import { firebaseFunctions } from './firebase'

export type DeferredWorkspaceActivityPayload = {
  workspaceId: string
  actorId: string
  actorName?: string | null
  targetType: string
  targetId: string
  targetTitle: string
  action: string
  summary: string
  board?: string | null
  metadata?: Record<string, unknown>
}

export type DeferredContributionPayload = {
  kind: 'TASK' | 'CHECKLIST'
  dateKey: string
}

export type DeferredExperiencePayload = {
  registryVersion: string
  event: {
    eventId: string
    action: string
    workspaceId?: string | null
    sourceId?: string | null
    occurredAt?: string
    payload?: Record<string, unknown>
    clientVisible?: boolean
    celebrationsEnabled?: boolean
  }
}

export type DeferredIntegrationPayload = {
  workspaceId: string
  providerId: string
  actionKey: string
  automationId?: string
}

export type DeferredClientEvent =
  | { clientEventId: string; actionKey: 'WORKSPACE_ACTIVITY' | 'UPLOAD_HUB_ACTIVITY'; kind: 'WORKSPACE_ACTIVITY'; payload: DeferredWorkspaceActivityPayload }
  | { clientEventId: string; actionKey: `CONTRIBUTION:${'TASK' | 'CHECKLIST'}`; kind: 'CONTRIBUTION'; payload: DeferredContributionPayload }
  | { clientEventId: string; actionKey: `EXPERIENCE:${string}`; kind: 'EXPERIENCE'; payload: DeferredExperiencePayload }
  | { clientEventId: string; actionKey: 'INTEGRATION_GATE_HIT' | 'INTEGRATION_MISSING'; kind: 'INTEGRATION'; payload: DeferredIntegrationPayload }

type StoredDeferredEvent = {
  id: string
  actionKey: DeferredEventActionKey
  kind: DeferredClientEvent['kind']
  iv: ArrayBuffer
  ciphertext: ArrayBuffer
  createdAt: number
  retryCount: number
  lastError: string | null
}

type FlushDecisionInput = Pick<StoredDeferredEvent, 'actionKey' | 'createdAt'>[]

type StoredKey = { id: 'active'; key: CryptoKey; createdAt: number }

type FlushResult = {
  results: Array<{ clientEventId: string; status: 'SUCCESS' | 'DUPLICATE' | 'PERMANENT_FAILURE' | 'RETRYABLE_FAILURE'; error?: string }>
}

const DB_NAME = 'timelining-deferred-events'
const DB_VERSION = 1
const STORE_EVENTS = 'events'
const STORE_KEYS = 'keys'

let dbPromise: Promise<IDBDatabase> | null = null
let flushTimer: number | null = null
let flushing = false

const hiddenFlushIntervalMs = () => Math.max(deferredEventBatchingConfig.maxWaitMs, deferredEventBatchingConfig.flushIntervalMs)

const supportsEncryptedOutbox = () =>
  typeof indexedDB !== 'undefined'
  && typeof crypto !== 'undefined'
  && Boolean(crypto.subtle)
  && typeof TextEncoder !== 'undefined'
  && typeof TextDecoder !== 'undefined'

const openDb = (): Promise<IDBDatabase> => {
  if (!supportsEncryptedOutbox()) return Promise.reject(new Error('Encrypted IndexedDB outbox is unavailable.'))
  if (dbPromise) return dbPromise
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_EVENTS)) {
        const store = db.createObjectStore(STORE_EVENTS, { keyPath: 'id' })
        store.createIndex('createdAt', 'createdAt', { unique: false })
      }
      if (!db.objectStoreNames.contains(STORE_KEYS)) db.createObjectStore(STORE_KEYS, { keyPath: 'id' })
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('Deferred outbox open failed.'))
  })
  return dbPromise
}

const runStore = async <T>(
  storeName: typeof STORE_EVENTS | typeof STORE_KEYS,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> => {
  const db = await openDb()
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(storeName, mode)
    const store = tx.objectStore(storeName)
    const request = work(store)
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('Deferred outbox request failed.'))
    tx.onerror = () => reject(tx.error ?? new Error('Deferred outbox transaction failed.'))
    tx.onabort = () => reject(tx.error ?? new Error('Deferred outbox transaction aborted.'))
  })
}

const getKey = async (): Promise<CryptoKey> => {
  const existing = await runStore<StoredKey | undefined>(STORE_KEYS, 'readonly', (store) =>
    store.get('active') as IDBRequest<StoredKey | undefined>,
  )
  if (existing?.key) return existing.key
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt'])
  await runStore<IDBValidKey>(STORE_KEYS, 'readwrite', (store) =>
    store.put({ id: 'active', key, createdAt: Date.now() } satisfies StoredKey),
  )
  return key
}

const encryptEvent = async (event: DeferredClientEvent) => {
  const key = await getKey()
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const encoded = new TextEncoder().encode(JSON.stringify(event))
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded)
  return { iv: iv.buffer, ciphertext }
}

const decryptEvent = async (record: StoredDeferredEvent): Promise<DeferredClientEvent> => {
  const key = await getKey()
  const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: record.iv }, key, record.ciphertext)
  return JSON.parse(new TextDecoder().decode(plaintext)) as DeferredClientEvent
}

const listStoredEvents = async (): Promise<StoredDeferredEvent[]> => {
  const all = await runStore<StoredDeferredEvent[]>(STORE_EVENTS, 'readonly', (store) =>
    store.getAll() as IDBRequest<StoredDeferredEvent[]>,
  )
  return all.sort((left, right) => left.createdAt - right.createdAt)
}

const deleteStoredEvent = (id: string) =>
  runStore<undefined>(STORE_EVENTS, 'readwrite', (store) => store.delete(id) as IDBRequest<undefined>)

const putStoredEvent = (record: StoredDeferredEvent) =>
  runStore<IDBValidKey>(STORE_EVENTS, 'readwrite', (store) => store.put(record))

const clearStoredEvents = () =>
  runStore<undefined>(STORE_EVENTS, 'readwrite', (store) => store.clear() as IDBRequest<undefined>)

const isOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false

export const shouldFlushDeferredEvents = (
  stored: FlushDecisionInput,
  options: { force?: boolean; now?: number } = {},
): boolean => {
  if (stored.length === 0) return false
  if (options.force) return true
  const actionCounts = new Map<DeferredEventActionKey, number>()
  stored.forEach((record) => actionCounts.set(record.actionKey, (actionCounts.get(record.actionKey) ?? 0) + 1))
  if (Array.from(actionCounts.values()).some((count) => count >= deferredEventBatchingConfig.minimumBatchSize)) return true
  if (deferredEventBatchingConfig.maxWaitMs <= 0) return false
  const oldestCreatedAt = Math.min(...stored.map((record) => record.createdAt))
  return (options.now ?? Date.now()) - oldestCreatedAt >= deferredEventBatchingConfig.maxWaitMs
}

export const enqueueDeferredEvent = async (
  event: Omit<DeferredClientEvent, 'clientEventId'> & { clientEventId?: string },
): Promise<boolean> => {
  if (!isDeferredEventActionEnabled(event.actionKey)) return false
  if (!supportsEncryptedOutbox()) return false
  const clientEventId = event.clientEventId ?? crypto.randomUUID().replaceAll('-', '_')
  const completeEvent = { ...event, clientEventId } as DeferredClientEvent
  try {
    const encrypted = await encryptEvent(completeEvent)
    await putStoredEvent({
      id: clientEventId,
      actionKey: completeEvent.actionKey,
      kind: completeEvent.kind,
      iv: encrypted.iv,
      ciphertext: encrypted.ciphertext,
      createdAt: Date.now(),
      retryCount: 0,
      lastError: null,
    })
    scheduleDeferredEventFlush(250)
    return true
  } catch (error) {
    console.warn('[deferred-events] enqueue failed; falling back to immediate call.', error)
    return false
  }
}

const getSubmitBatch = () => httpsCallable<{ events: DeferredClientEvent[] }, FlushResult>(firebaseFunctions, 'submitDeferredClientEvents')

export const flushDeferredEvents = async (options: { force?: boolean } = {}): Promise<void> => {
  if (flushing || !isOnline() || !supportsEncryptedOutbox()) return
  flushing = true
  try {
    const stored = (await listStoredEvents()).slice(0, deferredEventBatchingConfig.maxBatchSize)
    if (stored.length === 0) return
    if (!shouldFlushDeferredEvents(stored, options)) return
    const events: DeferredClientEvent[] = []
    const byId = new Map(stored.map((record) => [record.id, record]))
    for (const record of stored) {
      try {
        events.push(await decryptEvent(record))
      } catch (error) {
        console.warn('[deferred-events] dropping undecryptable event.', error)
        await deleteStoredEvent(record.id)
      }
    }
    if (events.length === 0) return
    const submitBatch = getSubmitBatch()
    const response = await submitBatch({ events })
    const remove = new Set<string>()
    const retry = new Map<string, string>()
    response.data.results.forEach((result) => {
      if (result.status === 'SUCCESS' || result.status === 'DUPLICATE' || result.status === 'PERMANENT_FAILURE') remove.add(result.clientEventId)
      else retry.set(result.clientEventId, result.error ?? 'Retryable deferred event failure.')
    })
    await Promise.all([
      ...Array.from(remove).map((id) => deleteStoredEvent(id)),
      ...Array.from(retry).map(async ([id, error]) => {
        const record = byId.get(id)
        if (!record) return
        await putStoredEvent({ ...record, retryCount: record.retryCount + 1, lastError: error })
      }),
    ])
  } catch (error) {
    const stored = await listStoredEvents().catch(() => [])
    await Promise.all(stored.slice(0, deferredEventBatchingConfig.maxBatchSize).map((record) =>
      putStoredEvent({ ...record, retryCount: record.retryCount + 1, lastError: error instanceof Error ? error.message : 'Deferred event flush failed.' }),
    ))
  } finally {
    flushing = false
  }
}

export const scheduleDeferredEventFlush = (delayMs = deferredEventBatchingConfig.flushIntervalMs): void => {
  if (typeof window === 'undefined') return
  if (flushTimer !== null) window.clearTimeout(flushTimer)
  const nextDelay = document.visibilityState === 'hidden' ? Math.max(delayMs, hiddenFlushIntervalMs()) : delayMs
  flushTimer = window.setTimeout(() => {
    flushTimer = null
    void flushDeferredEvents().finally(() => scheduleDeferredEventFlush())
  }, nextDelay)
}

export const startDeferredEventFlushLoop = (): (() => void) => {
  if (typeof window === 'undefined') return () => undefined
  const startupTimer = window.setTimeout(() => void flushDeferredEvents({ force: true }), 0)
  scheduleDeferredEventFlush()
  const online = () => void flushDeferredEvents({ force: true })
  const visibility = () => {
    if (document.visibilityState !== 'visible') return
    void flushDeferredEvents({ force: true }).finally(() => scheduleDeferredEventFlush())
  }
  window.addEventListener('online', online)
  document.addEventListener('visibilitychange', visibility)
  return () => {
    window.removeEventListener('online', online)
    document.removeEventListener('visibilitychange', visibility)
    window.clearTimeout(startupTimer)
    if (flushTimer !== null) window.clearTimeout(flushTimer)
    flushTimer = null
  }
}

export const deferredEventOutboxInternals = {
  clearStoredEvents,
  decryptEvent,
  encryptEvent,
  listStoredEvents,
  shouldFlushDeferredEvents,
}
